// ============================================================
// DM-LMS — Payments Pending Count Context
// AdminLayout owns the fetch and renders the nav badge from it. The count loads on mount;
// useVerifyPayment calls refetchPendingCount() after every settled payment (match, mismatch or
// manual) so the badge updates without the pages and AdminLayout knowing about each other.
// ============================================================

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { getJson } from "../hooks/useApi.js";
import type { PaginatedResult } from "@dm-lms/shared/types";

interface PaymentsPendingCountValue {
  pendingCount: number | null;
  refetchPendingCount: () => void;
}

const PaymentsPendingCountContext = createContext<PaymentsPendingCountValue>({
  pendingCount: null,
  refetchPendingCount: () => {},
});

export function PaymentsPendingCountProvider({ children }: { children: ReactNode }) {
  const [pendingCount, setPendingCount] = useState<number | null>(null);

  const refetchPendingCount = useCallback(() => {
    void getJson<PaginatedResult<unknown>>("/api/payments?status=pending&page=1&pageSize=1").then((res) => {
      if (res.ok) setPendingCount(res.data.total);
    });
  }, []);

  useEffect(() => {
    refetchPendingCount();
  }, [refetchPendingCount]);

  return (
    <PaymentsPendingCountContext.Provider value={{ pendingCount, refetchPendingCount }}>
      {children}
    </PaymentsPendingCountContext.Provider>
  );
}

export function usePaymentsPendingCount(): PaymentsPendingCountValue {
  return useContext(PaymentsPendingCountContext);
}
